import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { DeepReadonly } from 'ts-essentials';

import type { CustomerThunkAction } from '@jane/monolith/customer/customerAction';
import { NotificationsService } from '@jane/monolith/services/notifications';
import { IdentificationSource } from '@jane/monolith/sources/identification';
import type { Identification } from '@jane/shared/types';

import { updateUsersSuccess } from './users';

export const updateIdentification =
  (
    documentPart: Partial<Identification>,
    onSuccess?: () => void
  ): CustomerThunkAction =>
  (dispatch) => {
    dispatch(initiateUpdateIdentification());

    IdentificationSource.update({ document: documentPart }).then(
      ({ document, message }) => {
        const documentWithImmediateImages = {
          ...document,
          ...documentPart,
        };

        NotificationsService.success(message);
        dispatch(updateIdentificationSuccess(documentWithImmediateImages));
        onSuccess && onSuccess();
      },
      () => {
        dispatch(updateIdentificationError());
      }
    );
  };

export const getIdentification = (): CustomerThunkAction => (dispatch) =>
  IdentificationSource.get().then((result) =>
    dispatch(getIdentificationSuccess(result))
  );

export const uploadIdentification =
  ({
    key,
    value,
  }: {
    key: keyof Identification;
    value: string | null;
  }): CustomerThunkAction =>
  (dispatch) => {
    dispatch(initiateUploadIdentification(key));

    const data = {
      document: {
        [key]: value,
      },
    };

    IdentificationSource.update(data).then(
      () => dispatch(uploadIdentificationSuccess({ key, value })),
      () => dispatch(uploadIdentificationError(key))
    );
  };

export type IdentificationState = DeepReadonly<{
  document: Identification;
  documentHasLoaded: boolean;
  isUploading: boolean;
  loading_government_photo: boolean;
  loading_mmj_card: boolean;
  loading_mmj_card_back: boolean;
  loading_student_photo: boolean;
  loading_veteran_photo: boolean;
}>;

const getInitialState = (): IdentificationState => ({
  document: {
    first_name: '',
    last_name: '',
    birth_date: '',
    government_photo: null,
    mmj_card: null,
    mmj_card_back: null,
    student_photo: null,
    veteran_photo: null,
  },
  documentHasLoaded: false,
  isUploading: false,
  loading_government_photo: false,
  loading_mmj_card: false,
  loading_mmj_card_back: false,
  loading_student_photo: false,
  loading_veteran_photo: false,
});

const setValue = (
  state: IdentificationState,
  key: keyof Identification,
  value: string | number | null
) => {
  const document = {
    ...state.document,
    [key]: value,
  };
  return { ...state, document };
};

const identificationSlice = createSlice({
  name: 'identification',
  initialState: getInitialState(),
  reducers: {
    getIdentificationSuccess: (
      state,
      action: PayloadAction<{ document: Identification }>
    ) => {
      const document = { ...state.document, ...action.payload.document };
      return {
        ...state,
        document,
        documentHasLoaded: true,
      };
    },
    setIdentificationValue: (
      state,
      action: PayloadAction<{ name: keyof Identification; value: string }>
    ) => {
      return setValue(state, action.payload.name, action.payload.value);
    },
    initiateUpdateIdentification: (state) => {
      return { ...state, isUploading: true };
    },
    updateIdentificationSuccess: (
      state,
      action: PayloadAction<{ first_name: string; last_name: string }>
    ) => {
      return {
        ...state,
        isUploading: false,
        document: {
          ...state.document,
          ...action.payload,
        },
      };
    },
    updateIdentificationError: (state) => {
      return { ...state, isUploading: false };
    },
    initiateUploadIdentification: (state, action: PayloadAction<string>) => {
      return { ...state, [`loading_${action.payload}`]: true };
    },
    uploadIdentificationSuccess: (
      state,
      action: PayloadAction<{ key: keyof Identification; value: string | null }>
    ) => {
      return setValue(
        {
          ...state,
          [`loading_${action.payload.key}`]: false,
        },
        action.payload.key,
        action.payload.value
      );
    },
    uploadIdentificationError: (state, action: PayloadAction<unknown>) => {
      return { ...state, [`loading_${action.payload}`]: false };
    },
    updateBirthDate: (state, action: PayloadAction<string>) => {
      return setValue(state, 'birth_date', action.payload);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(updateUsersSuccess, (state, action) => {
      return {
        ...state,
        isUploading: false,
        document: {
          ...state.document,
          first_name: action.payload.first_name ?? '',
          last_name: action.payload.last_name ?? '',
        },
      };
    });
  },
  selectors: {
    selectIdentification: (state) => state,
  },
});

export const {
  reducer: identificationReducer,
  actions: {
    initiateUpdateIdentification,
    updateIdentificationSuccess,
    updateIdentificationError,
    initiateUploadIdentification,
    uploadIdentificationSuccess,
    uploadIdentificationError,
    getIdentificationSuccess,
    setIdentificationValue,
    updateBirthDate,
  },
  selectors: { selectIdentification },
} = identificationSlice;

export type IdentificationActions =
  | ReturnType<typeof initiateUpdateIdentification>
  | ReturnType<typeof updateIdentificationError>
  | ReturnType<typeof updateIdentificationSuccess>
  | ReturnType<typeof getIdentificationSuccess>
  | ReturnType<typeof initiateUploadIdentification>
  | ReturnType<typeof uploadIdentificationSuccess>
  | ReturnType<typeof uploadIdentificationError>
  | ReturnType<typeof updateBirthDate>
  | ReturnType<typeof setIdentificationValue>;
